import type { Company, FlattenedPortfolioItem } from "@/types";
import type { Cancellation, CancellationStatement } from "@/types/cancellation";
import { registrationAuthorityForRegion } from "@/utils/registryAccounts";
import { MONTH_END } from "./complianceGroupsFixture";
import type { CertificateLot, StatementFixture } from "./complianceGroupsTypes";

const DEMO_TIMESTAMP = "2026-09-10T00:00:00Z";

const MONTH_NUMBER: Record<string, string> = {
  Jan: "01",
  Feb: "02",
  Mar: "03",
  Apr: "04",
  May: "05",
  Jun: "06",
  Jul: "07",
  Aug: "08",
  Sep: "09",
  Oct: "10",
  Nov: "11",
  Dec: "12",
};

/** "6 May 2026" → "2026-05-06T00:00:00Z" */
export const toIsoTimestamp = (human: string): string => {
  const [day, mon, year] = human.split(" ");
  return `${year}-${MONTH_NUMBER[mon]}-${day.padStart(2, "0")}T00:00:00Z`;
};

const portfolioIds = new Map<string, number>();
const portfolioId = (name: string): number => {
  if (!portfolioIds.has(name)) portfolioIds.set(name, portfolioIds.size + 1);
  return portfolioIds.get(name)!;
};

/**
 * Shapes a fixture lot as the row type PortfolioItemsTable renders. Only fields the default and
 * transaction column sets read are meaningful; the rest carry neutral values.
 */
export const toFlattenedPortfolioItem = (
  lot: CertificateLot,
  index: number
): FlattenedPortfolioItem => ({
  id: index + 1,
  count: lot.n,
  company_order_id: null,
  reserved_by_active_order: false,
  updated_at: DEMO_TIMESTAMP,
  portfolio_id: portfolioId(lot.portfolio),
  portfolio_name: lot.portfolio,
  // VIRTUAL portfolios display their own name in the table instead of a translated type label.
  portfolio_type: "VIRTUAL",
  is_static_type: false,
  is_default: false,
  company_id: 1,
  company_name: "Northwind Demo",
  account_emails: [],
  certificate_id: index + 1,
  registry_id: null,
  transfer_deadline: `${lot.deadline}T00:00:00Z`,
  transfer_deadline_passed: false,
  sales_transaction_item_id: null,
  production_device_name: lot.device,
  device_group_id: null,
  device_group_name: null,
  fuel_code: null,
  technology_code: null,
  // i18n keys under common.technology are lowercase ("solar", "wind").
  technology_type: lot.tech.toLowerCase(),
  production_support: false,
  ecolabel_approved: false,
  registry_status: null,
  transferred_at: null,
  production_month: lot.month,
  registry_region: lot.registryCode,
  issuing_region: lot.registryCode,
  registration_authority: registrationAuthorityForRegion(lot.registryCode),
  limit_price: null,
  sale_price: null,
  sale_currency: null,
  status: lot.status.toUpperCase(),
  cod_age: null,
  okpower_new_compatible: false,
});

export const toFlattenedPortfolioItems = (
  lots: CertificateLot[]
): FlattenedPortfolioItem[] => lots.map(toFlattenedPortfolioItem);

/** The two French beneficiaries, as the minimal Company shape the statements table needs. */
export const STATEMENT_COMPANIES: Pick<Company, "id" | "name">[] = [
  { id: 1, name: "Northwind Demo France SAS" },
  { id: 2, name: "Northwind Demo France Data Services SAS" },
];

const companyIdFor = (name: string): number =>
  STATEMENT_COMPANIES.find((c) => c.name === name)?.id ?? 1;

const periodOf = (monthIndex: number) => {
  const mm = String(monthIndex + 1).padStart(2, "0");
  return { start: `2026-${mm}-01`, end: `2026-${mm}-${MONTH_END[monthIndex]}` };
};

export const toCancellation = (
  s: StatementFixture,
  index: number
): Cancellation => {
  const period = periodOf(s.monthIndex);
  return {
    id: index + 1,
    beneficiary_name: s.company,
    period_start: period.start,
    period_end: period.end,
    mwh_cancelled: s.mwh,
    cancellation_purpose: `Renewable electricity consumption of ${s.company} at ${s.address}`,
    // No fixture document exists behind the row, so the list must not promise one.
    uploaded_document_count: 0,
    created_at: toIsoTimestamp(s.cancelled),
    updated_at: toIsoTimestamp(s.received),
    company_id: companyIdFor(s.company),
    company_order_id: null,
    beneficiary_type: "CONSUMER",
    registry_code: s.registryCode,
    registry_country_region: "FR",
    cancellation_pdf_registry_url: null,
  };
};

export const toCancellationStatement = (
  s: StatementFixture,
  index: number
): CancellationStatement => {
  const base = toCancellation(s, index);
  return {
    id: base.id,
    company_id: base.company_id,
    company_order_id: null,
    beneficiary_name: base.beneficiary_name,
    beneficiary_type: base.beneficiary_type,
    registry_code: base.registry_code,
    registry_country_region: base.registry_country_region,
    address: s.address,
    period_start: base.period_start,
    period_end: base.period_end,
    cancellation_purpose: base.cancellation_purpose,
    mwh_cancelled: base.mwh_cancelled,
    created_at: base.created_at,
    updated_at: base.updated_at,
    cancellation_pdf_registry_url: null,
    uploaded_documents: [],
    portfolio_items: null,
  };
};
